import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Deep-linkable/refresh-stable active-tab state for a `TabbedPage` (docs/365-ADMIN-USER-
 * COMPLETION-AND-CATALOGS-TABS.md item 3) — the active tab lives in the `?tab=` query param
 * (not local `useState`), so a tab survives a browser refresh and can be linked/bookmarked
 * directly (`/provider-settings?tab=terms`), matching the admin's existing routed-page
 * discipline (every other piece of admin navigation state — the current route itself — is
 * already URL-driven).
 *
 * `tabIds` is the page's own ordered tab-id list; an unrecognized/missing `tab` param falls
 * back to `defaultTab` (or `tabIds[0]`) — never a blank/crashed tab panel. Setting the tab
 * back to the default REMOVES the param (`replace: true`, so tab switches don't pile up
 * browser-history entries) rather than writing a redundant `?tab=<default>`.
 */
export function useTabParam(tabIds: readonly string[], defaultTab?: string): [string, (id: string) => void] {
  const fallback = defaultTab ?? tabIds[0] ?? "";
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get("tab");
  const active = raw && tabIds.includes(raw) ? raw : fallback;

  const setActive = useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id === fallback) {
            next.delete("tab");
          } else {
            next.set("tab", id);
          }
          return next;
        },
        { replace: true },
      );
    },
    [fallback, setSearchParams],
  );

  return useMemo(() => [active, setActive], [active, setActive]);
}
